import { useMemo } from 'react';
import isEqual from 'react-fast-compare';
import {
  FilterToolbarGroup,
  FilterToolbarItemId,
} from 'src/components/Filters';
import { useInternalNotesFilter } from 'src/components/Filters/InternalNotesFilter';
import { useAppContext } from 'src/contexts/AppContext';
import { useContent } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector';
import { PosEnumMultiSelect } from 'src/core/POS/PosMultiSelect/PosEnumMultiSelect';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Slider } from 'src/core/ui/Slider';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import {
  MARKET_PLACE_TO_CID,
  MARKETPLACE_SALE_PAYMENT_STATE_TO_CID,
  SALES_STATUS_TO_CID,
  TICKET_TYPE_TO_CID,
  YES_NO_ENUM_FILTER_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import {
  InhandDateRangePresetNames,
  SaleDateRangeExactPresetNames,
  SaleDateRangeRelativePresetNames,
  StandardDateRangePresetNames,
  StandardDateRangePresetNamesV2,
} from 'src/utils/dateTimeUtils';
import { FromYesNoEnum, ToYesNoEnum } from 'src/utils/eventQueryUtils';
import { formatNumber } from 'src/utils/numberFormatter';
import {
  ActionOutboxEntityType,
  Feature,
  Marketplace,
  Permission,
  UIReportV2SaleQuery,
} from 'src/WebApiController';

import { RowFilter } from '../Filters/RowFilter';
import { SectionFilter } from '../Filters/SectionFilter';
import { CurrencyFilterMultiSelector } from '../Selectors/CurrencyFilterSelector';
import { DayOfWeekSelector } from '../Selectors/DayOfWeekSelector';
import { ListingNotesSelector } from '../Selectors/ListingNotesSelector';
import { SellerAccountEmployeeSelector } from '../Selectors/SellerAccountEmployeeSelector';
import {
  MAX_NUMBER_OF_TICKETS_HELD,
  MAX_NUMBER_OF_TICKETS_WASTED,
} from './MainFilterBar.constants';
import { useSaleReportTagFilters } from './Tags/useSaleReportTagFilters';
import { useCommonEventFilters } from './useCommonEventFilters';
import { usePurchaseInfoFilters } from './usePurchaseInfoFilters';

export const saleReportMandatoryFiltersToShow: FilterToolbarItemId[] = [
  'eventDates',
];

const PurchaseInfoFilterIdsToShow: FilterToolbarItemId[] = [
  'purchaseVendorId',
  'purchaseVendorAccountIds',
  'purchaseBuyerId',
  'purchaseDates',
  'purchaseCommissionUserId',
];

export function useSaleReportFilters({
  isHorizontalLayout,
}: {
  isHorizontalLayout?: boolean;
}) {
  const { loginContext } = useAppContext();

  const systemText = useContent(ContentId.System);

  const hasAutoFulfillSetting = useUserHasFeature(Feature.AutoFulfillSetting);
  const hasSeatSaverFeature = useUserHasFeature(Feature.SeatSaver);
  const hasDateFiltersRelativeExactCustomFeature = useUserHasFeature(
    Feature.DateFiltersRelativeExactCustom
  );

  const { initialQuery, tempQuery, setTempQuery } =
    useFilterQueryContext<UIReportV2SaleQuery>();

  const notesFilters = useInternalNotesFilter({
    entityType: ActionOutboxEntityType.Sale,
    isHorizontalLayout,
  });

  const seatingFilters = useMemo<FilterToolbarGroup>(
    () => ({
      titleContentId: ContentId.Seating,
      type: 'row',
      items: [
        {
          filterId: 'sectionContains',
          labelContentId: ContentId.Section,
          filterQueryKeys: ['sectionEquals', 'sectionContains'],
          filterItem: (
            <SectionFilter
              query={tempQuery}
              setQuery={setTempQuery}
              isHorizontalLayout={isHorizontalLayout}
            />
          ),
        },
        {
          filterId: 'rowContains',
          labelContentId: ContentId.Row,
          filterQueryKeys: ['rowEquals', 'rowContains'],
          filterItem: (
            <RowFilter
              query={tempQuery}
              setQuery={setTempQuery}
              isHorizontalLayout={isHorizontalLayout}
            />
          ),
        },
      ],
    }),
    [isHorizontalLayout, setTempQuery, tempQuery]
  );

  const purchaseInfoFilters = usePurchaseInfoFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    showCommissionerFilter: true,
    useRelativePresets: true,
  });

  const supportedPurchaseInfoFilters = useMemo(() => {
    const items = purchaseInfoFilters.items.filter((item) =>
      PurchaseInfoFilterIdsToShow.includes(item.filterId)
    );

    return {
      ...purchaseInfoFilters,
      items,
    };
  }, [purchaseInfoFilters]);

  const eventFilters = useCommonEventFilters({
    showCatalogFilters: true,
    showEventFilter: false,
    showVenueFilter: true,
    showPerformerFilter: true,
    forceUseCatalogV2: true,
    additionalFilters: [
      {
        filterId: 'numberOfTicketsHeld' as FilterToolbarItemId,
        labelContentId: ContentId.NumberOfTicketsHeld,
        filterQueryKeys: ['numberOfTicketsHeld'] as FilterToolbarItemId[],
        filterItem: (
          <Slider.Root
            min={0}
            max={MAX_NUMBER_OF_TICKETS_HELD}
            step={1}
            value={[
              tempQuery.numberOfTicketsHeld?.start ?? 0,
              tempQuery.numberOfTicketsHeld?.end ?? MAX_NUMBER_OF_TICKETS_HELD,
            ]}
            style={{ minWidth: '160px' }}
            renderHandle={(n) => (
              <Slider.Handle>{formatNumber(n)}</Slider.Handle>
            )}
            onValueChange={([start, end]: [number, number]) => {
              if (
                start !== (tempQuery.numberOfTicketsHeld?.start || 0) ||
                end !== tempQuery.numberOfTicketsHeld?.end
              ) {
                const newQuery = {
                  ...tempQuery,
                  numberOfTicketsHeld: {
                    start: start === 0 ? null : start,
                    end: end === MAX_NUMBER_OF_TICKETS_HELD ? null : end,
                  },
                };
                setTempQuery(newQuery);
              }
            }}
          />
        ),
      },
      {
        filterId: 'isParking' as FilterToolbarItemId,
        labelContentId: ContentId.IsParkingEvent,
        filterQueryKeys: ['isParking'] as FilterToolbarItemId[],
        filterItem: (
          <PosEnumSelect
            style={{ width: '100%' }}
            value={ToYesNoEnum(tempQuery.isParking)}
            placeholderText={ContentId.All}
            enableEmptySelection
            onChange={(yesNoEnumValue) => {
              if (yesNoEnumValue !== ToYesNoEnum(tempQuery.isParking)) {
                setTempQuery({
                  ...tempQuery,
                  isParking: FromYesNoEnum(yesNoEnumValue),
                });
              }
            }}
            valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
          />
        ),
      },
    ],
  });

  const tagsFilter = useSaleReportTagFilters({
    query: tempQuery,
    setQuery: setTempQuery,
  });

  return {
    filters: [
      eventFilters,
      {
        titleContentId: ContentId.Fulfillment,
        items: [
          {
            filterId: 'saleStatuses' as FilterToolbarItemId,
            labelContentId: ContentId.Status,
            filterQueryKeys: ['saleStatuses'] as FilterToolbarItemId[],
            filterItem: (
              <PosEnumMultiSelect
                triggerProps={{
                  style: { width: '100%' },
                }}
                values={tempQuery.saleStatuses ?? []}
                placeholderText={ContentId.AllSaleStatuses}
                valueOptionsContent={SALES_STATUS_TO_CID}
                onChange={({ newValues: saleStatuses }) => {
                  setTempQuery({
                    ...tempQuery,
                    saleStatuses,
                  });
                }}
              />
            ),
          },
          {
            filterId: 'quantityWasted' as FilterToolbarItemId,
            labelContentId: ContentId.WastedTickets,
            filterQueryKeys: ['quantityWasted'] as FilterToolbarItemId[],
            filterItem: (
              <Slider.Root
                min={0}
                max={MAX_NUMBER_OF_TICKETS_WASTED}
                step={1}
                value={[
                  tempQuery.quantityWasted?.start ?? 0,
                  tempQuery.quantityWasted?.end ?? MAX_NUMBER_OF_TICKETS_WASTED,
                ]}
                renderHandle={(n) => (
                  <Slider.Handle>{formatNumber(n)}</Slider.Handle>
                )}
                style={{ minWidth: '160px' }}
                onValueChange={([start, end]: [number, number]) => {
                  if (
                    start !== (tempQuery.quantityWasted?.start || 0) ||
                    end !== tempQuery.quantityWasted?.end
                  ) {
                    const newQuery = {
                      ...tempQuery,
                      quantityWasted: {
                        start: start === 0 ? null : start,
                        end: end === MAX_NUMBER_OF_TICKETS_WASTED ? null : end,
                      },
                    };
                    setTempQuery(newQuery);
                  }
                }}
              />
            ),
          },
          {
            filterId: 'listingNoteIds' as FilterToolbarItemId,
            labelContentId: ContentId.SeatTraits,
            filterQueryKeys: ['listingNoteIds'] as FilterToolbarItemId[],
            filterItem: (
              <ListingNotesSelector
                values={
                  tempQuery.listingNoteIds?.map((lnId) => lnId.toString()) ?? []
                }
                onChange={({ newValues: newIds }) => {
                  const newValues = newIds.length ? newIds.map(Number) : null;
                  if (newValues !== tempQuery.listingNoteIds) {
                    setTempQuery({
                      ...tempQuery,
                      listingNoteIds: newValues,
                    });
                  }
                }}
                allowSearchAsInput={false}
                showPills={false}
              />
            ),
          },
          {
            filterId: 'seatContains' as FilterToolbarItemId,
            labelContentId: ContentId.SeatNumberContains,
            filterQueryKeys: ['seatContains'] as FilterToolbarItemId[],
            filterItem: (
              <PosTextField
                style={{ width: '100%' }}
                value={tempQuery.seatContains ?? ''}
                type="text"
                onChange={(e) => {
                  const newValue = e.target.value?.length
                    ? e.target.value.trim()
                    : null;
                  if (tempQuery.seatContains !== newValue) {
                    setTempQuery({
                      ...tempQuery,
                      seatContains: newValue,
                    });
                  }
                }}
                maxLength={200}
              />
            ),
          },
          {
            filterId: 'inHandDates' as FilterToolbarItemId,
            labelContentId: ContentId.InHand,
            filterQueryKeys: ['inHandDates'] as FilterToolbarItemId[],
            filterItem: (
              <DateRangeSelector
                useRelativePresets={
                  // This is a deprecated prop. Remove once DateFiltersRelativeExactCustom is GA'ed
                  hasDateFiltersRelativeExactCustomFeature ? false : true
                }
                exactPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? InhandDateRangePresetNames
                    : undefined
                }
                trailingPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? InhandDateRangePresetNames
                    : undefined
                }
                presetNames={InhandDateRangePresetNames}
                value={tempQuery.inHandDates}
                defaultValue={initialQuery.inHandDates}
                onBlur={({ value }) =>
                  setTempQuery({
                    ...tempQuery,
                    inHandDates: value,
                  })
                }
              />
            ),
          },
          {
            filterId: 'ticketType' as FilterToolbarItemId,
            labelContentId: ContentId.TicketType,
            filterQueryKeys: ['ticketType'] as FilterToolbarItemId[],
            filterItem: (
              <PosEnumSelect
                style={{ width: '100%' }}
                value={tempQuery.ticketType}
                placeholderText={ContentId.AllTicketTypes}
                enableEmptySelection
                onChange={(ticketType) => {
                  if (ticketType !== tempQuery.ticketType) {
                    setTempQuery({
                      ...tempQuery,
                      ticketType: ticketType,
                    });
                  }
                }}
                valueOptionsContent={TICKET_TYPE_TO_CID}
              />
            ),
          },
          {
            filterId: 'fulfillerId' as FilterToolbarItemId,
            labelContentId: ContentId.FulfilledBy,
            filterQueryKeys: ['fulfillerSellerUserId'] as FilterToolbarItemId[],
            filterItem: (
              <SellerAccountEmployeeSelector
                value={tempQuery.fulfillerSellerUserId ?? undefined}
                enableEmptySelection
                style={{ width: '100%' }}
                onChange={(newId) => {
                  if (newId !== tempQuery.fulfillerSellerUserId) {
                    setTempQuery({
                      ...tempQuery,
                      fulfillerSellerUserId: newId,
                    });
                  }
                }}
              />
            ),
          },
          ...(hasSeatSaverFeature
            ? [
                {
                  filterId: 'isSeatSaver' as FilterToolbarItemId,
                  labelContentId: ContentId.Placeholder,
                  filterQueryKeys: ['isSeatSaver'] as FilterToolbarItemId[],
                  filterItem: (
                    <PosEnumSelect
                      style={{ width: '100%' }}
                      value={ToYesNoEnum(tempQuery.isSeatSaver)}
                      placeholderText={ContentId.All}
                      enableEmptySelection
                      onChange={(yesNoEnumValue) => {
                        if (
                          yesNoEnumValue !== ToYesNoEnum(tempQuery.isSeatSaver)
                        ) {
                          setTempQuery({
                            ...tempQuery,
                            isSeatSaver: FromYesNoEnum(yesNoEnumValue),
                          });
                        }
                      }}
                      valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                    />
                  ),
                },
              ]
            : []),
          {
            filterId: 'fulfillmentDates' as FilterToolbarItemId,
            labelContentId: ContentId.FulfillmentDate,
            filterQueryKeys: ['fulfillmentDates'] as FilterToolbarItemId[],
            filterItem: (
              <DateRangeSelector
                useRelativePresets={
                  // This is a deprecated prop. Remove once DateFiltersRelativeExactCustom is GA'ed
                  hasDateFiltersRelativeExactCustomFeature ? false : true
                }
                presetNames={StandardDateRangePresetNames}
                exactPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : undefined
                }
                trailingPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : undefined
                }
                value={tempQuery.fulfillmentDates}
                defaultValue={initialQuery.fulfillmentDates}
                onBlur={({ value }) =>
                  setTempQuery({
                    ...tempQuery,
                    fulfillmentDates: value,
                  })
                }
              />
            ),
          },
          ...(hasAutoFulfillSetting
            ? [
                {
                  filterId: 'isNoFulfill' as FilterToolbarItemId,
                  labelContentId: ContentId.AutoFulfill,
                  type: 'row',
                  filterQueryKeys: ['isNoFulfill'] as FilterToolbarItemId[],
                  filterItem: (
                    <PosEnumSelect
                      style={{ width: '100%' }}
                      value={ToYesNoEnum(
                        // the ui is the inverse of isNoFulfil
                        tempQuery.isNoFulfill == null
                          ? null
                          : !tempQuery.isNoFulfill
                      )}
                      placeholderText={ContentId.All}
                      enableEmptySelection
                      onChange={(yesNoEnumValue) => {
                        if (
                          yesNoEnumValue !==
                          ToYesNoEnum(
                            // the ui is the inverse of isNoFulfil
                            tempQuery.isNoFulfill == null
                              ? null
                              : !tempQuery.isNoFulfill
                          )
                        ) {
                          const v = FromYesNoEnum(yesNoEnumValue);
                          setTempQuery({
                            ...tempQuery,
                            isNoFulfill: v == null ? null : !v, // the ui is the inverse of isNoFulfil
                          });
                        }
                      }}
                      valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                    />
                  ),
                },
              ]
            : []),
          {
            filterId: 'cancellationDates' as FilterToolbarItemId,
            labelContentId: ContentId.CancellationDate,
            filterQueryKeys: ['cancellationDates'] as FilterToolbarItemId[],
            filterItem: (
              <DateRangeSelector
                useRelativePresets={
                  // This is a deprecated prop. Remove once DateFiltersRelativeExactCustom is GA'ed
                  hasDateFiltersRelativeExactCustomFeature ? false : true
                }
                presetNames={StandardDateRangePresetNames}
                exactPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : undefined
                }
                trailingPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : undefined
                }
                value={tempQuery.cancellationDates}
                defaultValue={initialQuery.cancellationDates}
                onBlur={({ value }) =>
                  setTempQuery({
                    ...tempQuery,
                    cancellationDates: value,
                  })
                }
              />
            ),
          },
        ],
      },
      {
        titleContentId: ContentId.WhenAndWhere,
        type: 'row',
        items: [
          {
            filterId: 'soldOnMarketplace' as FilterToolbarItemId,
            labelContentId: ContentId.Marketplace,
            filterQueryKeys: ['soldOnMarketplace'] as FilterToolbarItemId[],
            filterItem: (
              <PosEnumSelect
                style={{ width: '100%' }}
                value={tempQuery.soldOnMarketplace}
                placeholderText={ContentId.AllMerchants}
                enableEmptySelection
                onChange={(soldOnMarketplace) => {
                  if (soldOnMarketplace !== tempQuery.soldOnMarketplace) {
                    setTempQuery({
                      ...tempQuery,
                      soldOnMarketplace,
                    });
                  }
                }}
                valueOptionsContent={(
                  loginContext?.user?.activeAccount?.marketplaceSettings?.map(
                    (m) => m.mkp
                  ) ?? [Marketplace.StubHub]
                )
                  .concat(Marketplace.Offline)
                  .concat(Marketplace.Wastage)
                  .reduce(
                    (cur, m) => {
                      cur[m] = MARKET_PLACE_TO_CID[m];
                      return cur;
                    },
                    {} as Record<Marketplace, string>
                  )}
              />
            ),
          },
          {
            filterId: 'saleDates' as FilterToolbarItemId,
            labelContentId: ContentId.SaleDate,
            filterQueryKeys: ['saleDates'] as FilterToolbarItemId[],
            filterItem: (
              <DateRangeSelector
                presetNames={SaleDateRangeRelativePresetNames}
                value={tempQuery.saleDates}
                exactPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : SaleDateRangeExactPresetNames
                }
                trailingPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : SaleDateRangeRelativePresetNames
                }
                defaultValue={initialQuery.saleDates}
                onBlur={({ value }) => {
                  setTempQuery({
                    ...tempQuery,
                    saleDates: value,
                  });
                }}
                useRelativePresets={
                  // This is a deprecated prop. Remove once DateFiltersRelativeExactCustom is GA'ed
                  hasDateFiltersRelativeExactCustomFeature ? false : true
                }
              />
            ),
          },
          {
            filterId: 'dayOfWeek' as FilterToolbarItemId,
            labelContentId: ContentId.SaleDayOfWeek,
            filterQueryKeys: ['dayOfWeek'] as FilterToolbarItemId[],
            filterItem: (
              <DayOfWeekSelector
                value={tempQuery.dayOfWeek?.toString() ?? undefined}
                enableEmptySelection
                style={{ width: '100%' }}
                onChange={(newId) => {
                  if (
                    (newId ?? null) !==
                    (tempQuery.dayOfWeek?.toString() ?? null)
                  ) {
                    setTempQuery({
                      ...tempQuery,
                      dayOfWeek: newId ? Number(newId) : null,
                    });
                  }
                }}
              />
            ),
          },
          {
            filterId: 'listingInsertBy' as FilterToolbarItemId,
            labelContentId: ContentId.OriginatedBy,
            filterQueryKeys: ['listingInsertBy'] as FilterToolbarItemId[],
            filterItem: (
              <SellerAccountEmployeeSelector
                value={tempQuery.listingInsertBy ?? undefined}
                enableEmptySelection
                style={{ width: '100%' }}
                onChange={(newId) => {
                  if (newId !== tempQuery.listingInsertBy) {
                    setTempQuery({
                      ...tempQuery,
                      listingInsertBy: newId,
                    });
                  }
                }}
              />
            ),
          },
          {
            filterId: 'insertBy' as FilterToolbarItemId,
            labelContentId: ContentId.CreatedBy,
            filterQueryKeys: ['insertBy'] as FilterToolbarItemId[],
            filterItem: (
              <SellerAccountEmployeeSelector
                value={tempQuery.insertBy ?? undefined}
                unchangedText={systemText}
                allowUnchanged
                enableEmptySelection
                style={{ width: '100%' }}
                onChange={(newId) => {
                  if (newId !== tempQuery.insertBy) {
                    setTempQuery({
                      ...tempQuery,
                      insertBy: newId,
                    });
                  }
                }}
              />
            ),
          },
          {
            filterId: 'listingIdContains' as FilterToolbarItemId,
            labelContentId: ContentId.ListingId,
            filterQueryKeys: ['listingIdContains'] as FilterToolbarItemId[],
            filterItem: (
              <PosTextField
                style={{ width: '100%' }}
                value={tempQuery.listingIdContains ?? ''}
                type="text"
                onChange={(e) => {
                  const newValue = e.target.value?.length
                    ? e.target.value.trim()
                    : null;
                  if (tempQuery.listingIdContains !== newValue) {
                    setTempQuery({
                      ...tempQuery,
                      listingIdContains: newValue,
                    });
                  }
                }}
                maxLength={200}
              />
            ),
          },
          {
            filterId: 'pricerSellerUserId' as FilterToolbarItemId,
            filterQueryKeys: ['pricerSellerUserId'] as FilterToolbarItemId[],
            labelContentId: ContentId.PricedBy,
            filterItem: (
              <SellerAccountEmployeeSelector
                value={tempQuery.pricerSellerUserId ?? undefined}
                enableEmptySelection
                style={{ width: '100%' }}
                onChange={(newId) => {
                  if (newId !== tempQuery.pricerSellerUserId) {
                    setTempQuery({
                      ...tempQuery,
                      pricerSellerUserId: newId,
                    });
                  }
                }}
              />
            ),
          },
          {
            filterId: 'saleIdContains' as FilterToolbarItemId,
            labelContentId: ContentId.SaleId,
            filterQueryKeys: ['saleIdContains'] as FilterToolbarItemId[],
            filterItem: (
              <PosTextField
                style={{ width: '100%' }}
                value={tempQuery.saleIdContains ?? ''}
                type="text"
                onChange={(e) => {
                  const newValue = e.target.value?.length
                    ? e.target.value.trim()
                    : null;
                  if (tempQuery.saleIdContains !== newValue) {
                    setTempQuery({
                      ...tempQuery,
                      saleIdContains: newValue,
                    });
                  }
                }}
                maxLength={200}
              />
            ),
          },
          {
            filterId: 'marketplaceSaleIdContains' as FilterToolbarItemId,
            labelContentId: ContentId.ExternalSaleId,
            filterQueryKeys: [
              'marketplaceSaleIdContains',
            ] as FilterToolbarItemId[],
            filterItem: (
              <PosTextField
                style={{ width: '100%' }}
                value={tempQuery.marketplaceSaleIdContains ?? ''}
                type="text"
                onChange={(e) => {
                  const newValue = e.target.value?.length
                    ? e.target.value.trim()
                    : null;
                  if (tempQuery.marketplaceSaleIdContains !== newValue) {
                    setTempQuery({
                      ...tempQuery,
                      marketplaceSaleIdContains: newValue,
                    });
                  }
                }}
                maxLength={200}
              />
            ),
          },
          {
            filterId: 'purchaseOrderIdContains' as FilterToolbarItemId,
            labelContentId: ContentId.PurchaseId,
            filterQueryKeys: [
              'purchaseOrderIdContains',
            ] as FilterToolbarItemId[],
            filterItem: (
              <PosTextField
                style={{ width: '100%' }}
                value={tempQuery.purchaseOrderIdContains ?? ''}
                type="text"
                onChange={(e) => {
                  const newValue = e.target.value?.length
                    ? e.target.value.trim()
                    : null;
                  if (tempQuery.purchaseOrderIdContains !== newValue) {
                    setTempQuery({
                      ...tempQuery,
                      purchaseOrderIdContains: newValue,
                    });
                  }
                }}
                maxLength={200}
              />
            ),
          },
          {
            filterId: 'vendorOrderIdContains' as FilterToolbarItemId,
            labelContentId: ContentId.ExternalPurchaseId,
            filterQueryKeys: ['vendorOrderIdContains'] as FilterToolbarItemId[],
            filterItem: (
              <PosTextField
                style={{ width: '100%' }}
                value={tempQuery.vendorOrderIdContains ?? ''}
                type="text"
                onChange={(e) => {
                  const newValue = e.target.value?.length
                    ? e.target.value.trim()
                    : null;
                  if (tempQuery.vendorOrderIdContains !== newValue) {
                    setTempQuery({
                      ...tempQuery,
                      vendorOrderIdContains: newValue,
                    });
                  }
                }}
                maxLength={200}
              />
            ),
          },
        ],
      },
      {
        titleContentId: ContentId.Payment,
        requiredPermissions: [Permission.Sales_ViewPaymentState],
        type: 'row',
        items: [
          {
            filterId: 'marketplacePaymentState' as FilterToolbarItemId,
            labelContentId: ContentId.Payment,
            filterQueryKeys: [
              'marketplacePaymentState',
            ] as FilterToolbarItemId[],
            filterItem: (
              <PosEnumSelect
                style={{ width: '100%' }}
                value={tempQuery.marketplacePaymentState}
                placeholderText={ContentId.AllPaymentStates}
                enableEmptySelection
                onChange={(marketplacePaymentState) => {
                  if (
                    marketplacePaymentState !==
                    tempQuery.marketplacePaymentState
                  ) {
                    setTempQuery({
                      ...tempQuery,
                      marketplacePaymentState: marketplacePaymentState,
                    });
                  }
                }}
                valueOptionsContent={MARKETPLACE_SALE_PAYMENT_STATE_TO_CID}
              />
            ),
          },
          {
            filterId: 'paymentDates' as FilterToolbarItemId,
            labelContentId: ContentId.PaymentDate,
            filterQueryKeys: ['paymentDates'] as FilterToolbarItemId[],
            filterItem: (
              <DateRangeSelector
                useRelativePresets={
                  // This is a deprecated prop. Remove once DateFiltersRelativeExactCustom is GA'ed
                  hasDateFiltersRelativeExactCustomFeature ? false : true
                }
                presetNames={StandardDateRangePresetNames}
                exactPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : undefined
                }
                trailingPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : undefined
                }
                value={tempQuery.paymentDates}
                defaultValue={initialQuery.paymentDates}
                onBlur={({ value }) =>
                  setTempQuery({
                    ...tempQuery,
                    paymentDates: value,
                  })
                }
              />
            ),
          },
          {
            filterId: 'paymentReferenceId' as FilterToolbarItemId,
            labelContentId: ContentId.PaymentReference,
            filterQueryKeys: ['paymentReferenceId'] as FilterToolbarItemId[],
            filterItem: (
              <PosTextField
                style={{ width: '100%' }}
                value={tempQuery.paymentReferenceId ?? ''}
                type="text"
                onChange={(e) => {
                  const newValue = e.target.value ?? null;
                  if (tempQuery.paymentReferenceId !== newValue) {
                    setTempQuery({
                      ...tempQuery,
                      paymentReferenceId: newValue,
                    });
                  }
                }}
                maxLength={200}
              />
            ),
          },
          {
            filterId: 'currencyCodes' as FilterToolbarItemId,
            labelContentId: ContentId.Currency,
            filterQueryKeys: ['currencyCodes'] as FilterToolbarItemId[],
            filterItem: (
              <CurrencyFilterMultiSelector
                triggerProps={{ style: { width: '100%' } }}
                values={tempQuery.currencyCodes ?? []}
                placeholderText={ContentId.All}
                onChange={({ newValues: newCurrencyCodes }) => {
                  if (!isEqual(tempQuery.currencyCodes, newCurrencyCodes)) {
                    setTempQuery({
                      ...tempQuery,
                      currencyCodes: newCurrencyCodes?.length
                        ? newCurrencyCodes
                        : null,
                    });
                  }
                }}
                enableEmptySelection
              />
            ),
          },
        ],
      },
      seatingFilters,
      supportedPurchaseInfoFilters,
      ...(notesFilters ? [notesFilters] : []),
      ...tagsFilter,
    ],
  };
}
